<template>
  <div class="purchase-details-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header: Breadcrumb + Title + Badges + Actions -->
    <div class="page-header-container mb-5">
      <!-- Breadcrumb Bar -->
      <PageBreadcrumb :items="[{ text: 'Purchase', to: '/PurchaseList' }, { text: 'Purchase Details' }]" class="mb-1" />

      <!-- Main Header Row -->
      <div class="d-flex flex-wrap align-center justify-space-between gap-3">
        <div class="d-flex align-center flex-wrap gap-2">
          <v-btn
            icon
            class="back-btn mr-1"
            @click="$router.push('/PurchaseList')"
            title="Back to Purchase List"
          >
            <v-icon size="20" color="#475569">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="d-flex align-center flex-wrap gap-2">
            <span v-if="paymentList && paymentList.billNo" class="bill-badge">
              Bill #{{ (paymentList.billPrefix || '') + paymentList.billNo }}
            </span>
            <span
              v-if="isSettled"
              class="status-pill status-settled"
            >
              <span class="status-dot dot-settled"></span>
              Fully Settled
            </span>
            <span
              v-else
              class="status-pill status-not-settled"
            >
              <span class="status-dot dot-not-settled"></span>
              Payment Pending
            </span>
          </div>
        </div>

        <!-- Header Action Buttons -->
        <div class="header-actions d-flex align-center flex-wrap gap-2">
          <v-btn
            outlined
            depressed
            class="btn-compact action-outline-btn"
            @click="$router.push('/PurchaseList')"
          >
            <v-icon left x-small class="action-btn-icon">mdi-format-list-bulleted</v-icon>
            Purchase List
          </v-btn>
          <v-btn
            dark
            depressed
            class="btn-compact primary-action-btn"
            @click="$router.push('/Purchase')"
          >
            <v-icon left x-small>mdi-plus</v-icon>
            New Purchase
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Summary Metrics Strip (4 Sleek Airy Cards) -->
    <v-layout wrap class="mb-5 mx-n2">
      <!-- Card 1: Bill & Date Info -->
      <v-flex xs12 sm6 md3 class="pa-2">
        <v-card class="stat-card fill-height" elevation="0">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="stat-card-title">Bill & Purchase</span>
            <div class="stat-icon-badge badge-amber">
              <v-icon size="17" color="#9a6b27">mdi-receipt-text-outline</v-icon>
            </div>
          </div>
          <div class="stat-primary-value">
            {{ paymentList && paymentList.billNo ? ((paymentList.billPrefix || '') + paymentList.billNo) : '-' }}
          </div>
          <div class="stat-meta d-flex align-center">
            <v-icon size="13" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
            <span class="stat-meta-label">Purchase:</span>
            <span class="stat-meta-value ml-1">{{ paymentList && paymentList.purchaseDate ? formatDate(paymentList.purchaseDate) : '-' }}</span>
          </div>
        </v-card>
      </v-flex>

      <!-- Card 2: Invoice Info -->
      <v-flex xs12 sm6 md3 class="pa-2">
        <v-card class="stat-card fill-height" elevation="0">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="stat-card-title">Invoice Details</span>
            <div class="stat-icon-badge badge-blue">
              <v-icon size="17" color="#2563eb">mdi-file-document-outline</v-icon>
            </div>
          </div>
          <div class="stat-primary-value">
            {{ paymentList && paymentList.invoiceNo ? paymentList.invoiceNo : '-' }}
          </div>
          <div class="stat-meta d-flex align-center">
            <v-icon size="13" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
            <span class="stat-meta-label">Invoice:</span>
            <span class="stat-meta-value ml-1">{{ paymentList && paymentList.invoiceDate ? formatDate(paymentList.invoiceDate) : '-' }}</span>
          </div>
        </v-card>
      </v-flex>

      <!-- Card 3: Weight Breakdown -->
      <v-flex xs12 sm6 md3 class="pa-2">
        <v-card class="stat-card fill-height" elevation="0">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="stat-card-title">Net Weight</span>
            <div class="stat-icon-badge badge-golden">
              <v-icon size="17" color="#d97706">mdi-scale-balance</v-icon>
            </div>
          </div>
          <div class="stat-primary-value text-amber-dark">
            {{ paymentList && paymentList.totalNetWeight !== undefined ? paymentList.totalNetWeight : '0' }} <span class="unit-text">gm</span>
          </div>
          <div class="stat-meta d-flex align-center flex-wrap gap-1">
            <span class="meta-pill">Gross {{ paymentList ? paymentList.totalGrossWeight || 0 : 0 }}g</span>
            <span class="meta-pill">Stone {{ paymentList ? paymentList.totalStoneWeight || 0 : 0 }}g</span>
          </div>
        </v-card>
      </v-flex>

      <!-- Card 4: Financial Overview -->
      <v-flex xs12 sm6 md3 class="pa-2">
        <v-card class="stat-card fill-height" elevation="0">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="stat-card-title">Stone Charges</span>
            <div class="stat-icon-badge badge-emerald">
              <v-icon size="17" color="#059669">mdi-currency-inr</v-icon>
            </div>
          </div>
          <div class="stat-primary-value text-emerald-dark">
            ₹ {{ paymentList && paymentList.totalStonePrice !== undefined ? Number(paymentList.totalStonePrice).toLocaleString() : '0' }}
          </div>
          <div class="stat-meta d-flex align-center">
            <span class="stat-meta-label mr-1">Status:</span>
            <span :class="isSettled ? 'status-pill status-settled' : 'status-pill status-not-settled'">
              <span class="status-dot" :class="isSettled ? 'dot-settled' : 'dot-not-settled'"></span>
              {{ isSettled ? 'Settled' : 'Pending' }}
            </span>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- SECTION 1: Purchased Items Table -->
    <v-card class="section-card mb-5" elevation="0">
      <div class="section-header d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <div class="section-icon-badge mr-3">
            <v-icon size="17" color="#9a6b27">mdi-package-variant-closed</v-icon>
          </div>
          <div>
            <h2 class="section-title">Purchased Items</h2>
            <div class="section-subtitle">Item breakdown and stone details for this purchase bill</div>
          </div>
        </div>
        <span class="count-badge">{{ dataList ? dataList.length : 0 }} {{ (dataList && dataList.length === 1) ? 'Item' : 'Items' }}</span>
      </div>

      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 46px;">#</th>
              <th class="text-left">Design</th>
              <th class="text-center">Count</th>
              <th class="text-right">Item/Wt (gm)</th>
              <th class="text-right">Net Wt (gm)</th>
              <th class="text-right">Cost (%)</th>
              <th class="text-right">Stone Price (₹)</th>
              <th class="text-right">Stone Wt (gm)</th>
              <th class="text-right">Touch Rate (%)</th>
              <th class="text-right">Thankam (gm)</th>
              <th class="text-center">Status</th>
              <th class="text-right">Pending Thankam</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in dataList"
              :key="i"
              class="table-row"
            >
              <!-- S.No -->
              <td class="text-center cell-muted">{{ i + 1 }}</td>

              <!-- Design -->
              <td class="text-left">
                <span v-if="value.designId && value.designId.code" class="code-pill">
                  {{ value.designId.code }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Count -->
              <td class="text-center font-weight-medium text-dark">
                {{ value.count || '-' }}
              </td>

              <!-- Item / Wt -->
              <td class="text-right text-muted-dark">
                {{ value.itemPerWeight || '-' }}
              </td>

              <!-- Net Wt -->
              <td class="text-right font-weight-bold text-dark">
                {{ value.netWeight ? Number(value.netWeight).toFixed(2) : '-' }}
              </td>

              <!-- Cost -->
              <td class="text-right text-muted-dark">
                {{ value.cost ? value.cost + '%' : '-' }}
              </td>

              <!-- Stone Price -->
              <td class="text-right font-weight-medium">
                {{ value.stonePrice ? '₹ ' + Number(value.stonePrice).toLocaleString() : '-' }}
              </td>

              <!-- Stone Wt -->
              <td class="text-right text-muted-dark">
                {{ value.stoneWeight || '-' }}
              </td>

              <!-- Touch Rate -->
              <td class="text-right font-weight-medium">
                {{ value.touchRate ? value.touchRate + '%' : '-' }}
              </td>

              <!-- Thankam -->
              <td class="text-right font-weight-bold text-dark">
                {{ value.thankam ? Number(value.thankam).toFixed(2) : '-' }}
              </td>

              <!-- Status -->
              <td class="text-center">
                <span
                  v-if="value.settleStatus === 'Settled'"
                  class="status-pill status-settled"
                >
                  <span class="status-dot dot-settled"></span>
                  Settled
                </span>
                <span
                  v-else-if="value.settleStatus === 'notSettled'"
                  class="status-pill status-not-settled"
                >
                  <span class="status-dot dot-not-settled"></span>
                  Not Settled
                </span>
                <span
                  v-else-if="value.settleStatus === 'partiallySettled'"
                  class="status-pill status-partially-settled"
                >
                  <span class="status-dot dot-partially-settled"></span>
                  Partially Settled
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Pending Thankam -->
              <td class="text-right font-weight-bold" :class="value.tobePaidThankam > 0 ? 'text-amber-dark' : 'text-muted'">
                {{ value.tobePaidThankam ? Number(value.tobePaidThankam).toFixed(2) : '0.00' }}
              </td>
            </tr>

            <tr v-if="!dataList || dataList.length === 0">
              <td colspan="12" class="text-center py-8 text-muted">
                <v-icon medium color="#9ca3af" class="mb-2">mdi-package-variant</v-icon>
                <div style="font-size: 13.5px; font-weight: 500;">No item records found for this purchase</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- SECTION 2: Purchase Settlement -->
    <v-card class="section-card mb-5" elevation="0">
      <div
        class="section-header d-flex align-center justify-space-between cursor-pointer"
        @click="isexpand = !isexpand"
      >
        <div class="d-flex align-center">
          <div class="section-icon-badge mr-3">
            <v-icon size="17" color="#9a6b27">mdi-calculator-variant-outline</v-icon>
          </div>
          <div>
            <div class="d-flex align-center gap-2">
              <h2 class="section-title">Purchase Settlement</h2>
              <span class="status-pill status-partially-settled">Settlement Action</span>
            </div>
            <div class="section-subtitle">Configure touch rates, thankam rates and settle purchase items</div>
          </div>
        </div>
        <v-btn icon small class="accordion-toggle-btn">
          <v-icon size="20" color="#64748b" :class="{ 'rotate-180': isexpand }">
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="isexpand" class="settlement-container">
          <detailPagePurchseSettlement />
        </div>
      </v-expand-transition>
    </v-card>

    <!-- SECTION 3: Payment History -->
    <v-card class="section-card" elevation="0">
      <div class="section-header d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <div class="section-icon-badge mr-3">
            <v-icon size="17" color="#9a6b27">mdi-cash-check</v-icon>
          </div>
          <div>
            <h2 class="section-title">Payment History</h2>
            <div class="section-subtitle">Historical settlement transactions and thankam balance records</div>
          </div>
        </div>
        <span class="count-badge">{{ settlementList ? settlementList.length : 0 }} {{ (settlementList && settlementList.length === 1) ? 'Record' : 'Records' }}</span>
      </div>

      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 46px;">#</th>
              <th class="text-left">Payment Date</th>
              <th class="text-right">Thankam Rate (₹)</th>
              <th class="text-right">Settled Thankam (gm)</th>
              <th class="text-right">Amount Paid (₹)</th>
              <th class="text-right">Excess Thankam (gm)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, j) in settlementList"
              :key="j"
              class="table-row"
            >
              <!-- Index -->
              <td class="text-center cell-muted">{{ j + 1 }}</td>

              <!-- Date -->
              <td class="text-left font-weight-medium text-dark">
                {{ item.create_date ? formatDate(item.create_date) : '-' }}
              </td>

              <!-- Thankam Rate -->
              <td class="text-right font-weight-medium">
                {{ item.thankamRate ? '₹ ' + Number(item.thankamRate).toFixed(2) : '-' }}
              </td>

              <!-- Settled Thankam -->
              <td class="text-right font-weight-bold text-dark">
                {{ item.settledThankam ? Number(item.settledThankam).toFixed(2) : '-' }}
              </td>

              <!-- Amount Paid -->
              <td class="text-right font-weight-bold text-emerald-dark">
                {{ item.payAmount ? '₹ ' + Number(item.payAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' }}
              </td>

              <!-- Excess Thankam -->
              <td class="text-right">
                <span v-if="item.balanceThankam" class="font-weight-medium text-dark">
                  {{ Number(item.balanceThankam).toFixed(2) }} gm
                </span>
                <span v-else class="text-muted">0.00</span>
              </td>
            </tr>

            <tr v-if="!settlementList || settlementList.length === 0">
              <td colspan="6" class="text-center py-8 text-muted">
                <v-icon medium color="#9ca3af" class="mb-2">mdi-cash-clock</v-icon>
                <div style="font-size: 13.5px; font-weight: 500;">No payment records found for this purchase</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import detailPagePurchseSettlement from "./detailPagePurchseSettlement";
import axios from "axios";

export default {
  components: {
    detailPagePurchseSettlement,
  },
  data() {
    return {
      isexpand: true,
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      Pagelength: null,
      dataList: [],
      paymentList: {},
      settlementList: [],
      isSettled: false,
    };
  },
  mounted() {
    this.getData();
    this.getPayment();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/purchaseItem/details",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
          purchaseId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.dataList = response.data.data || [];
            this.paymentList = response.data.purchasebills || {};
            this.isSettled = response.data.purchasebills ? response.data.purchasebills.isSettled : false;
            this.Pagelength = Math.ceil((response.data.count || 1) / 20);
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    getPayment() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/purchase/paymentdetails",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
          purchaseId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.settlementList = response.data.payment || [];
            this.Pagelength = Math.ceil((response.data.count || 1) / 20);
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    formatDate(item) {
      if (!item) return "-";
      var dt = new Date(item);
      if (isNaN(dt.getTime())) return item;
      var day = dt.getDate();
      var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      var month = months[dt.getMonth()];
      var year = dt.getFullYear();
      return day + " " + month + " " + year;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap");

.purchase-details-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  padding-bottom: 32px;
}

/* Page Header */
.page-header-container {
  padding: 0 2px;
}

.back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.back-btn:hover {
  background-color: #fff7ed;
  border-color: #fed7aa;
  transform: translateX(-2px);
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.2px;
}

.bill-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 9px;
  border-radius: 6px;
}

.breadcrumbs-sub {
  font-size: 12px;
}

.breadcrumb-link {
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: #9a6b27;
}

.breadcrumb-current {
  font-weight: 600;
  color: #0f172a;
}

/* Buttons */
.btn-compact {
  height: 34px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  padding: 0 14px !important;
  border-radius: 8px !important;
  text-transform: none !important;
}

.primary-action-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
  border-radius: 10px !important;
}

.primary-action-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px);
}

.action-outline-btn {
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  transition: all 0.2s ease !important;
}

.action-outline-btn .action-btn-icon {
  color: #64748b !important;
  transition: color 0.2s ease;
}

.action-outline-btn:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fffaf5 !important;
  transform: translateY(-1px);
}

.action-outline-btn:hover .action-btn-icon {
  color: #9a6b27 !important;
}

/* Stat Cards (Airy, Spacious, Modern) */
.stat-card {
  background-color: #ffffff;
  border: 1px solid #ede8e0;
  border-radius: 14px !important;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05) !important;
  transform: translateY(-2px);
}

.stat-card-title {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.stat-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-amber {
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
}

.badge-blue {
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
}

.badge-golden {
  background-color: #fffbeb;
  border: 1px solid #fef3c7;
}

.badge-emerald {
  background-color: #ecfdf5;
  border: 1px solid #d1fae5;
}

.stat-primary-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin: 4px 0 10px 0;
}

.stat-meta {
  font-size: 12px;
  color: #64748b;
  min-height: 22px;
}

.stat-meta-label {
  color: #64748b;
  font-weight: 400;
}

.stat-meta-value {
  color: #0f172a;
  font-weight: 500;
}

.meta-pill {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 500;
  color: #475569;
}

.unit-text {
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
}

.text-amber-dark {
  color: #b45309 !important;
}

.text-emerald-dark {
  color: #047857 !important;
}

.text-dark {
  color: #0f172a !important;
}

.text-muted-dark {
  color: #475569 !important;
}

/* Section Cards */
.section-card {
  background-color: #ffffff;
  border: 1px solid #ede8e0;
  border-radius: 16px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

.section-header {
  background-color: #faf8f5;
  border-bottom: 1px solid #ede8e0;
  padding: 14px 20px;
}

.section-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
}

.count-badge {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.accordion-toggle-btn {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

/* Custom Table (Airy, Spacious, Sleek) */
.table-responsive {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 4px;
}

/* Sleek subtle scrollbar for tables */
.table-responsive::-webkit-scrollbar {
  height: 5px;
  width: 5px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #cbd5e1 !important;
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #94a3b8 !important;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #f8fafc;
}

.custom-table th {
  padding: 12px 14px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.custom-table td {
  padding: 13px 14px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
  vertical-align: middle;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

.cell-muted {
  color: #94a3b8;
  font-size: 12px;
}

.code-pill {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  display: inline-block;
}

/* Status Badges */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.4;
  gap: 5px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.status-settled {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.dot-settled {
  background-color: #10b981;
}

.status-not-settled {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.dot-not-settled {
  background-color: #ef4444;
}

.status-partially-settled {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.dot-partially-settled {
  background-color: #f59e0b;
}

.text-muted {
  color: #9ca3af;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.settlement-container {
  padding: 0;
}
</style>